/**
 * SOP / series sin Pixel Data (PR/GSPS, SR, KO, Encapsulated PDF…).
 * No deben pasar por cornerstone.loadAndCacheImage.
 *
 * DOC/PDF como modalidad NO implica no-imagen: puede ser Secondary Capture
 * (JPG/PNG) → Cornerstone, o Encapsulated PDF → PdfViewport.
 */

/** Prefijo SOP Class de Softcopy Presentation State (…11.x). */
const PRESENTATION_STATE_PREFIX = '1.2.840.10008.5.1.4.1.1.11'

/** Encapsulated PDF Storage */
export const ENCAPSULATED_PDF_SOP = '1.2.840.10008.5.1.4.1.1.104.1'
/** Encapsulated CDA Storage */
export const ENCAPSULATED_CDA_SOP = '1.2.840.10008.5.1.4.1.1.104.2'

/** Prefijo Secondary Capture Image Storage (…1.1.7 / …1.1.7.x). */
const SECONDARY_CAPTURE_PREFIX = '1.2.840.10008.5.1.4.1.1.7'

/**
 * Modalidades que nunca tienen Pixel Data renderizable por Cornerstone.
 * DOC/PDF no están aquí: se clasifican por SOP.
 */
const NON_IMAGE_MODALITIES = new Set([
  'PR',
  'SR',
  'KO',
  'ECG', // a menudo waveform; no usar createImage
])

/** SOP Class UIDs conocidos sin imagen (además del prefijo PR). */
const NON_IMAGE_SOP_CLASSES = new Set([
  ENCAPSULATED_PDF_SOP,
  ENCAPSULATED_CDA_SOP,
  // Key Object Selection
  '1.2.840.10008.5.1.4.1.1.88.59',
  // Basic Text SR / Enhanced SR / Comprehensive (familia 88.*)
  '1.2.840.10008.5.1.4.1.1.88.11',
  '1.2.840.10008.5.1.4.1.1.88.22',
  '1.2.840.10008.5.1.4.1.1.88.33',
  '1.2.840.10008.5.1.4.1.1.88.34',
  '1.2.840.10008.5.1.4.1.1.88.35',
  '1.2.840.10008.5.1.4.1.1.88.40',
  '1.2.840.10008.5.1.4.1.1.88.50',
  '1.2.840.10008.5.1.4.1.1.88.67',
  '1.2.840.10008.5.1.4.1.1.88.68',
  '1.2.840.10008.5.1.4.1.1.88.69',
  '1.2.840.10008.5.1.4.1.1.88.70',
  '1.2.840.10008.5.1.4.1.1.88.71',
  '1.2.840.10008.5.1.4.1.1.88.72',
  '1.2.840.10008.5.1.4.1.1.88.73',
  '1.2.840.10008.5.1.4.1.1.88.74',
  '1.2.840.10008.5.1.4.1.1.88.75',
])

/**
 * @param {object|null|undefined} meta
 * @returns {string}
 */
function resolveSopClassUID(meta) {
  if (!meta || typeof meta !== 'object') return ''
  return String(
    meta.sopClassUID ||
      meta.SOPClassUID ||
      (meta.series && (meta.series.sopClassUID || meta.series.SOPClassUID)) ||
      (meta.inst && (meta.inst.sopClassUID || meta.inst.SOPClassUID)) ||
      ''
  ).trim()
}

/**
 * @param {object|null|undefined} meta
 * @returns {string}
 */
function resolveModality(meta) {
  if (!meta || typeof meta !== 'object') return ''
  return String(
    (meta.series && (meta.series.modality || meta.series.Modality)) ||
      meta.modality ||
      meta.Modality ||
      (meta.inst && (meta.inst.modality || meta.inst.Modality)) ||
      ''
  )
    .trim()
    .toUpperCase()
}

/**
 * @param {string|null|undefined} sopClassUID
 * @returns {boolean}
 */
export function isEncapsulatedPdfSop(sopClassUID) {
  const u = String(sopClassUID || '').trim()
  return u === ENCAPSULATED_PDF_SOP
}

/**
 * @param {string|null|undefined} sopClassUID
 * @returns {boolean}
 */
export function isSecondaryCaptureSop(sopClassUID) {
  const u = String(sopClassUID || '').trim()
  if (!u) return false
  return u === SECONDARY_CAPTURE_PREFIX || u.indexOf(SECONDARY_CAPTURE_PREFIX + '.') === 0
}

/**
 * @param {string|null|undefined} sopClassUID
 * @returns {boolean}
 */
export function isNonImageSopClass(sopClassUID) {
  const u = String(sopClassUID || '').trim()
  if (!u) return false
  if (u === PRESENTATION_STATE_PREFIX || u.indexOf(PRESENTATION_STATE_PREFIX + '.') === 0) {
    return true
  }
  if (NON_IMAGE_SOP_CLASSES.has(u)) return true
  // Familia SR 1.2.840.10008.5.1.4.1.1.88.*
  if (u.indexOf('1.2.840.10008.5.1.4.1.1.88.') === 0) return true
  return false
}

/**
 * @param {string|null|undefined} modality
 * @returns {boolean}
 */
export function isNonImageModality(modality) {
  return NON_IMAGE_MODALITIES.has(String(modality || '').trim().toUpperCase())
}

/**
 * Meta de Encapsulated PDF (visor PdfViewport).
 * @param {object|null|undefined} meta
 * @returns {boolean}
 */
export function isPdfDocumentMeta(meta) {
  if (!meta || typeof meta !== 'object') return false
  if (meta.isPdfDocument === true || meta.renderKind === 'pdf') return true
  if (meta.series && meta.series.isPdfDocument === true) return true
  return isEncapsulatedPdfSop(resolveSopClassUID(meta))
}

/**
 * @param {object|null|undefined} fileItem
 * @returns {boolean}
 */
export function isPdfDocumentFileItem(fileItem) {
  return isPdfDocumentMeta(fileItem)
}

/**
 * @param {object|null|undefined} series
 * @param {object|null|undefined} inst
 * @returns {boolean}
 */
export function isPdfDocumentSeriesInst(series, inst) {
  return isPdfDocumentMeta({
    series: series || {},
    ...((inst && typeof inst === 'object') ? inst : {}),
    sopClassUID: (inst && (inst.sopClassUID || inst.SOPClassUID)) || undefined,
    modality:
      (series && (series.modality || series.Modality)) ||
      (inst && (inst.modality || inst.Modality)) ||
      undefined,
  })
}

/**
 * DOC/informe con Pixel Data (Secondary Capture u otra SOP imagen).
 * @param {object|null|undefined} meta
 * @returns {boolean}
 */
export function isImageDocMeta(meta) {
  if (!meta || typeof meta !== 'object') return false
  if (isPdfDocumentMeta(meta)) return false
  const sop = resolveSopClassUID(meta)
  if (isSecondaryCaptureSop(sop)) return true
  const mod = resolveModality(meta)
  if ((mod === 'DOC' || mod === 'PDF') && sop && !isNonImageSopClass(sop)) {
    return true
  }
  return false
}

/**
 * Meta de instancia/serie (manifest o file item).
 * SOP prevalece sobre flags stale (p.ej. DOC+SC mal marcado).
 * @param {object|null|undefined} meta
 * @returns {boolean}
 */
export function isNonImageMeta(meta) {
  if (!meta || typeof meta !== 'object') return false

  const sop = resolveSopClassUID(meta)
  if (sop) {
    if (isEncapsulatedPdfSop(sop) || sop === ENCAPSULATED_CDA_SOP) return true
    if (isNonImageSopClass(sop)) return true
    if (isSecondaryCaptureSop(sop)) return false
  }

  if (meta.renderKind === 'pdf' || meta.isPdfDocument === true) return true
  if (meta.series && meta.series.isPdfDocument === true) return true

  // DOC/PDF con SOP de imagen (no encapsulado) → Cornerstone
  const mod = resolveModality(meta)
  if ((mod === 'DOC' || mod === 'PDF') && sop && !isNonImageSopClass(sop)) {
    return false
  }

  if (isNonImageModality(mod)) return true

  // Sin SOP decisivo: respetar flags de stub/manifest
  if (!sop) {
    if (meta.nonImage === true || meta.isNonImage === true) return true
    if (meta.series && meta.series.isNonImage === true) return true
  }
  return false
}

/**
 * @param {object|null|undefined} fileItem
 * @returns {boolean}
 */
export function isNonImageFileItem(fileItem) {
  return isNonImageMeta(fileItem)
}

/**
 * @param {object|null|undefined} series
 * @param {object|null|undefined} inst
 * @returns {boolean}
 */
export function isNonImageSeriesInst(series, inst) {
  return isNonImageMeta({
    series: series || {},
    ...((inst && typeof inst === 'object') ? inst : {}),
    sopClassUID: (inst && (inst.sopClassUID || inst.SOPClassUID)) || undefined,
    modality:
      (series && (series.modality || series.Modality)) ||
      (inst && (inst.modality || inst.Modality)) ||
      undefined,
  })
}

/**
 * Marca file item in-place.
 * @param {object} fileItem
 * @returns {object}
 */
export function stampNonImageOnFileItem(fileItem) {
  if (!fileItem) return fileItem
  const isPdf = isPdfDocumentFileItem(fileItem)
  const flag = isNonImageFileItem(fileItem)
  fileItem.nonImage = flag
  fileItem.isPdfDocument = isPdf
  if (isPdf) {
    fileItem.renderKind = 'pdf'
  }
  if (!fileItem.series) fileItem.series = {}
  fileItem.series.isNonImage = flag
  fileItem.series.isPdfDocument = isPdf
  if (flag && !fileItem.sopClassUID) {
    const sop =
      (fileItem.series && fileItem.series.sopClassUID) ||
      null
    if (sop) fileItem.sopClassUID = sop
  }
  return fileItem
}

/**
 * Modalidad de un file item (manifest / store).
 * @param {object|null|undefined} fileItem
 * @returns {string}
 */
export function fileItemModality(fileItem) {
  if (!fileItem || typeof fileItem !== 'object') return ''
  return String(
    (fileItem.series && (fileItem.series.modality || fileItem.series.Modality)) ||
      fileItem.modality ||
      fileItem.Modality ||
      ''
  )
    .trim()
    .toUpperCase()
}

/**
 * Badge corto para Explorer (PR / SR / KO / DOC / PDF …).
 * @param {object|null|undefined} fileItem
 * @returns {string}
 */
export function explorerNonImageBadge(fileItem) {
  if (isPdfDocumentFileItem(fileItem)) {
    const mod = fileItemModality(fileItem)
    if (mod === 'DOC') return 'DOC'
    return 'PDF'
  }
  const mod = fileItemModality(fileItem)
  if (isNonImageModality(mod)) return mod
  if (mod === 'DOC' || mod === 'PDF') return mod
  if (isNonImageFileItem(fileItem)) return 'PR'
  return ''
}

/**
 * Placeholder SVG etiquetado (evita rectángulo negro vacío).
 * @param {string} badge
 * @returns {string} data URL
 */
export function buildNonImageThumbnailDataUrl(badge) {
  const label = String(badge || 'PR').slice(0, 6)
  let sub = 'sin imagen'
  if (label === 'PR') sub = 'GSPS'
  else if (label === 'PDF' || label === 'DOC') sub = 'documento'
  const esc = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="180" height="220" viewBox="0 0 180 220">` +
    `<rect width="180" height="220" fill="#1a2228"/>` +
    `<rect x="14" y="48" width="152" height="124" rx="8" fill="#243038" stroke="#5a7a8c" stroke-width="1.5"/>` +
    `<text x="90" y="108" text-anchor="middle" fill="#d8e8f0" font-family="system-ui,sans-serif" font-size="28" font-weight="700">${esc(label)}</text>` +
    `<text x="90" y="136" text-anchor="middle" fill="#8aa0b0" font-family="system-ui,sans-serif" font-size="12">${esc(sub)}</text>` +
    `</svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}
